import moment from 'moment'
import {
  get,
  isObject,
  isUndefined,
  set,
  unset,
  isEmpty,
  intersection,
  isArray,
  keys
} from 'lodash'

export const getInitialState = (sections, elementType) =>
  sections.reduce(
    (acc, { val, id, key, value, elements, type, selected }, i) => {
      const objKey = id ?? key
      if (elements) {
        if (
          ['radio', 'autocomplete'].includes(type) &&
          !isUndefined(selected)
        ) {
          return {
            ...acc,
            [objKey]: selected
          }
        }

        return {
          ...acc,
          [objKey]: getInitialState(elements, type)
        }
      } else {
        if (elementType === 'toggle') {
          return value ?? false
        }

        // single choice
        if (['number', 'radio'].includes(elementType)) {
          return i === 0 ? val || value : acc
        }

        if (elementType === 'autocomplete') {
          return null
        }

        if (elementType === 'date') {
          return {
            ...acc,
            [objKey]: moment(val || value).format('YYYY/MM/DD')
          }
        }
        return { ...acc, [key ?? val]: value ?? true } // checkbox case
      }
    },
    {}
  )

export const getVisibiltyRules = (sections, hierarchy = []) =>
  sections.reduce((acc, section) => {
    const { id, key, elements, visibility } = section

    const objKey = id ?? key
    const pathToValue = buildPathToValue(hierarchy, objKey)

    if (visibility) {
      acc.push({
        section,
        field: pathToValue,
        rules: visibility
      })
    }

    if (elements) {
      return [...acc, ...getVisibiltyRules(elements, pathToValue)]
    }

    return acc
  }, [])

export const isAsyncSection = (section, asyncSections) =>
  keys(asyncSections).includes(section)

export const getAsyncDependencies = (sections) =>
  sections?.reduce((acc, section) => {
    if (section.asyncDeps) {
      return { ...acc, [section.id]: section.asyncDeps }
    } else return acc
  }, {})

export const applyVisibilityRules = (filters, visibiltyRules) => {
  const clonedFilters = { ...filters }

  for (const rule of visibiltyRules) {
    const { field, rules, section } = rule

    if (!shouldBeVisible(clonedFilters, rules)) {
      unset(clonedFilters, field)
    } else if (isUndefined(get(clonedFilters, field))) {
      const id = section.id ?? section.key
      const newState = get(getInitialState([section]), id)

      set(clonedFilters, field, newState)
    }
  }

  return clonedFilters
}

// The item is visible if at least a condition passes
export const shouldBeVisible = (filters, rules) => {
  for (const rule of rules) {
    const { hierarchy, value } = rule
    const filterValue = get(filters, hierarchy)

    if (filterValue === value || filterValue?.[value]) {
      return true
    }
  }

  return false
}

// Items with nested elements are grouped inside arrays to go into nested accordions
export const groupSectionItems = (elements) => {
  let accordionItems = []
  let regularItems = []
  const itemGroups = []

  elements.forEach((elementToFind) => {
    if (elementToFind.elements) {
      if (regularItems.length > 0) {
        itemGroups.push({ type: 'regular', data: regularItems })
        regularItems = []
      }
      accordionItems.push(elementToFind)
    } else {
      if (accordionItems.length > 0) {
        itemGroups.push({ type: 'accordion', data: accordionItems })
        accordionItems = []
      }
      regularItems.push(elementToFind)
    }
  })

  if (accordionItems.length > 0) {
    itemGroups.push({ type: 'accordion', data: accordionItems })
  }

  if (regularItems.length > 0) {
    itemGroups.push({ type: 'regular', data: regularItems })
  }

  return itemGroups
}

export const isEveryElementCheckboxType = (elements) =>
  !elements.some((item) => item.type !== 'checkbox')

export const flatFiltersValues = (values = []) =>
  values.reduce((acc, item) => {
    if (isObject(item)) {
      return [...acc, ...flatFiltersValues(Object.values(item))]
    }
    return [...acc, item]
  }, [])

export const updateRecursevely = (filterObject, checked) =>
  Object.keys(filterObject).reduce((acc, key) => {
    if (isObject(filterObject[key])) {
      return {
        ...acc,
        [key]: updateRecursevely(filterObject[key], checked)
      }
    }

    return { ...acc, [key]: checked }
  }, {})

export const isStringMatching = (str, searchText) =>
  str.toLowerCase().includes(searchText)

export const isElementMatching = ({ item: { val, label }, searchText }) =>
  isStringMatching(label, searchText) || isStringMatching(`${val}`, searchText)

// The path is passed as an array to avoid problems with points in the keys
// For example, if the val is 1910.0, the hierarchy `group.1910.0` fails,
// While ['group', '1910.0'] goes fine
export const buildPathToValue = (hierarchy = [], value) => [...hierarchy, value]

export const getPreservedState = (oldFilters, newFilters) => {
  const keysInCommon = intersection(
    Object.keys(oldFilters),
    Object.keys(newFilters)
  )

  const toPreserve = keysInCommon.reduce(
    (acc, key) => ({
      ...acc,
      [key]: getPreservedValues(oldFilters[key], newFilters[key])
    }),
    {}
  )

  return Object.assign(newFilters, toPreserve)
}

const getPreservedValues = (oldValues, newValues) =>{
  if(isObject(newValues)) { //checkbox
    const newValuesUpdated = {...newValues}

    Object.keys(oldValues).forEach((key)=>{
      //if a value is present in both old and new section -> the value is the old one
      if(newValues.hasOwnProperty(key)){
        newValuesUpdated[key] = oldValues[key]
      }else{
        //if a value is present in the old section, but not in the new one -> the value is removed
        //I just want to preserve the values in common
        delete newValuesUpdated[key]
      }
    })

    //the new section has more values than the old section -> they get the standard value
    Object.keys(newValues).forEach((key)=>{
      if(!oldValues.hasOwnProperty(key)){
        newValuesUpdated[key] = true
      }
    })
    return newValuesUpdated
  }else {
    return oldValues
  }
}

export const findSectionElement = (section, hierarchy) => {
  let elementToFind = section

  for (const key of hierarchy) {
    const searchItem = isArray(elementToFind)
      ? elementToFind
      : elementToFind?.elements

    if (isEmpty(searchItem)) {
      return elementToFind
    }

    const index = searchItem.findIndex(({ id, key: itemKey, val }) =>
      [id, itemKey, val].includes(key)
    )

    if (index < 0) {
      return elementToFind
    }

    elementToFind = isEmpty(elementToFind.elements)
      ? elementToFind[index]
      : elementToFind.elements[index]
  }

  return elementToFind
}

export const buildNewSectionRecursevely = (sectionElement, checked) => {
  if (sectionElement.elements) {
    return buildNewSectionRecursevely(sectionElement.elements, checked)
  }

  for (const element of sectionElement) {
    if (element.elements) {
      buildNewSectionRecursevely(element.elements, checked)
    } else {
      element.value = checked
    }
  }
}

export const serverSideT = (t, namespace) => (key) => t(`${namespace}:${key}`)
